import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import CoinsIcon from '../../../../../../../components/icons/CoinsIcon';
import Button from '../../../../../../../components/global/Button';
import PhotoFinderCreditItemsTable from './components/PhotoFinderCreditItemsTable';
import usePhotoFinderCreditItemsCheckout from './hooks/usePhotoFinderCreditItemsCheckout';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../../../components/global/StripeCheckout';
import Spinner from '../../../../../../../components/global/Spinner';
import { getAsset } from '../../../../../../../util/assets';
import InfoCircleIcon from '../../../../../../../components/icons/InfoCircleIcon';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';

const propTypes = {
  title: PropTypes.string,
  basename: PropTypes.string.isRequired,
  onCheckoutSuccess: PropTypes.func,
  onPayPerPhoto: PropTypes.func,
  onRequestClose: PropTypes.func,
};

const PhotoFinderCreditItemsCheckout = ({
  title = 'Need more Photo Finder credits?',
  basename,
  onCheckoutSuccess,
  onPayPerPhoto,
  onRequestClose,
}) => {
  const history = useHistory();
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const {
    account,
    selectedItem,
    setSelectedItem,
    creditItems,
    submitting,
    onCheckout,
  } = usePhotoFinderCreditItemsCheckout({ onCheckoutSuccess });
  const activeSubscription = account?.photo_manager_subscriptions?.records?.[0];
  const waivedPhotoManager = !!account?.pro_settings?.has_free_photo_manager;

  const onPurchase = () => {
    if (account.default_payment_method) {
      onCheckout({ stripeToken: undefined });
    } else {
      openModal(STRIPE_CHECKOUT_MODAL, {
        description: (
          <p>
            Credits Purchase &ndash; You will receive your credits immediately.
          </p>
        ),
        buttonLabel: `Confirm & Pay - $${formatPriceForDisplay(
          selectedItem?.price?.value,
          true,
        )}`,
        onSuccess: token =>
          onCheckout({
            stripeToken: token,
          }),
        defaultName: account?.name ?? '',
        defaultEmail: account?.email_address ?? '',
      });
    }
  };

  if (!account) {
    return (
      <div className="text-center px-6 py-6">
        <Spinner />
      </div>
    );
  }

  if (!activeSubscription && !waivedPhotoManager) {
    return (
      <div className="text-center">
        <img
          width="44px"
          className="mb-2"
          src={getAsset('/waldo-icon-error.svg')}
          alt="Waldo Icon"
        />
        <h4>Oops!</h4>
        <p>
          It looks like you have not set up your Photo Manager subscription.
          This is needed before purchasing credits
        </p>

        <Button
          btnStyle="link"
          btnBlock
          onClick={() => {
            history.push(`/app/${basename}/account/settings/photo-manager`);
            onRequestClose?.();
          }}
        >
          Check my subscription settings
        </Button>
      </div>
    );
  }

  return (
    <div className="text-center">
      <CoinsIcon color="#0d84fe" size={48} />
      <div className="d-flex justify-content-center align-items-center mt-1 mb-0">
        <h4 className="mb-0">{title}</h4>
        <Button
          btnStyle="reset"
          className="d-flex ml-1"
          onClick={() =>
            openAlert({
              type: 'info',
              title: (
                <h4 className="text-black">What are Photo Finder Credits?</h4>
              ),
              message: (
                <p className="mt-1">
                  If you&apos;d like to deliver matched photos at no cost to
                  your users, you&apos;ll want to be sure and create your event
                  gallery using Photo Finder credits and then purchase your
                  preferred bundle here
                </p>
              ),
            })
          }
        >
          <InfoCircleIcon size={16} />
        </Button>
      </div>
      <p className="text-xs text-light">The more you buy, the more you save!</p>
      <p className="mb-1">Choose your # of credits below:</p>
      <PhotoFinderCreditItemsTable
        selectable
        photoFinderCreditItems={creditItems}
        onSelectionChange={item => setSelectedItem(item)}
      />
      <p className="text-left text-xs text-light mb-0">
        *1 credit = one photo officially published to an album.
      </p>
      <p className="text-left text-xs text-light mb-0">
        **Individual credits are $.20 per photo processed.
      </p>
      <p className="text-left text-xs text-light">
        ***Credits expire 365 days from the day of purchase.
      </p>

      <div className="row px-2 mb-2">
        {onPayPerPhoto && (
          <div className="col-12 col-md-6 px-0 pr-md-1">
            <Button btnOutline btnBlock onClick={onPayPerPhoto}>
              Pay per Photo
            </Button>
          </div>
        )}
        <div
          className={classNames({
            'col-12': true,
            'col-md-6 px-0 pl-md-1': !!onPayPerPhoto,
          })}
        >
          <Button
            disabled={submitting || !selectedItem}
            btnBlock={!!onPayPerPhoto}
            onClick={onPurchase}
          >
            {submitting
              ? 'Processing...'
              : `Continue${
                  selectedItem
                    ? ` (Pay $${formatPriceForDisplay(
                        selectedItem.price.value,
                      )})`
                    : ''
                }`}
          </Button>
        </div>
      </div>
    </div>
  );
};

PhotoFinderCreditItemsCheckout.propTypes = propTypes;

export default PhotoFinderCreditItemsCheckout;
